import { Link, Navigate, useParams } from "react-router-dom";
import { BookOpen, ArrowLeft } from "lucide-react";
import MarkdownLite, { parseBlocks, slugify } from "../components/MarkdownLite";
import { MANUALS, manualsFor } from "../help";
import { isFleetDomain } from "../config/productConfig";

// Páginas de ajuda (/ajuda e /ajuda/:manual). O acesso (só utilizadores com sessão, por perfil) é
// controlado nas rotas em App.js; esta página não depende de App (evita import circular).
// No domínio de frota só existe o manual do Aerofleet Center.
const shell = "min-h-screen bg-slate-950 text-slate-200";

function Header({ title }) {
  return (
    <header className="sticky top-0 z-10 border-b border-slate-800 bg-slate-950/90 backdrop-blur">
      <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
        <BookOpen className="h-5 w-5 text-sky-400" />
        <span className="font-semibold text-white" data-testid="help-title">{title}</span>
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? window.history.back() : (window.location.href = "/"))}
          className="ml-auto inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white"
          data-testid="help-back"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar
        </button>
      </div>
    </header>
  );
}

export function HelpIndex() {
  const fleet = isFleetDomain();
  const list = manualsFor(fleet);
  return (
    <div className={shell}>
      <Header title={fleet ? "Ajuda · Aerofleet Center" : "Ajuda"} />
      <main className="mx-auto max-w-4xl px-4 py-8">
        <h1 className="mb-6 text-2xl font-semibold text-white">Manuais</h1>
        <div className="grid gap-3 sm:grid-cols-2">
          {list.map(([slug, m]) => (
            <Link key={slug} to={`/ajuda/${slug}`} data-testid={`help-card-${slug}`} className="rounded-lg border border-slate-800 bg-slate-900 p-4 hover:border-sky-500/60">
              <div className="font-medium text-white">{m.title}</div>
              <div className="mt-1 text-sm text-slate-400">{m.subtitle}</div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}

export default function HelpPage() {
  const { manual } = useParams();
  const fleet = isFleetDomain();
  const entry = MANUALS[manual];
  // Desconhecido, ou de outro produto neste domínio: volta à lista.
  if (!entry || Boolean(entry.fleet) !== fleet) return <Navigate to="/ajuda" replace />;

  const toc = parseBlocks(entry.source).filter((b) => b.type === "h" && b.level === 2);
  return (
    <div className={shell}>
      <Header title={entry.title} />
      <main className="mx-auto max-w-4xl px-4 py-8">
        <nav className="mb-6 rounded-lg border border-slate-800 bg-slate-900 p-4" aria-label="Índice" data-testid="help-toc">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Índice</div>
          <ul className="grid gap-1 sm:grid-cols-2">
            {toc.map((h) => (
              <li key={h.text}>
                <a href={`#${slugify(h.text)}`} className="text-sm text-sky-400 hover:underline">{h.text}</a>
              </li>
            ))}
          </ul>
        </nav>
        <article data-testid="help-body"><MarkdownLite source={entry.source} /></article>
      </main>
    </div>
  );
}
